/* Rythme & éligibilité — RJ-07 · REAL TIER (Bible §26 · audit §7).
   Batch 13/14: this screen is now a READER. The participation window is one
   value per establishment, set in Établissement › Réglages and kept in the
   seam (`@/lib/scope`); here the merchant sees what it costs him — the euro
   line is the point, it turns an interval into a budget decision — and one
   link to the single place that changes it. Two editors for one value is how
   a workspace ends up disagreeing with itself.
   The presets are the same business shapes (`FENETRE_PRESETS`): a merchant
   knows what kind of business they run; they do not know whether 21 days is
   right for a salon. The interval stays visible beside the name.
   Batch 15 (D-66): the floor is `sectorFloor()`, not a constant — a café's
   rhythm is a day, a garage's two months, and this page says the merchant's
   own. The sector's name comes from the seam too; the rhythm endpoint used to
   carry a second copy of it and no longer does. */
import { useNavigate } from 'react-router-dom'
import { Button, Card, ErrorState, KV, Note, Pill } from '@/ui'
import { useLocations } from '@/lib/api'
import {
  ALL, FENETRE_PRESETS, sectorFloor, secteurLabel, useScope, useSecteur, useWindowDays, windowSentence,
} from '@/lib/scope'
import { useRhythm } from './api'
import { Chargement, Retour } from './parts'

const euros = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} €`

export default function Rythme() {
  const { data, isPending, isError, refetch } = useRhythm()
  const { data: locations } = useLocations()
  const [scope] = useScope()
  const nav = useNavigate()

  const lieux = (locations ?? []).filter(l => l.status !== 'inactive')
  const lieu = (scope !== ALL ? lieux.find(l => l.id === scope) : undefined) ?? lieux[0]
  const jours = useWindowDays(lieu?.id ?? 'l0')
  const secteur = useSecteur()
  const plancher = sectorFloor()

  if (isPending) return <Chargement />
  if (isError) return (
    <ErrorState what="Impossible de charger le rythme."
      action={<Button variant="primary" size="sm" onClick={() => refetch()}>Réessayer</Button>} />
  )

  const d = data
  const forme = FENETRE_PRESETS.find(p => p.days === jours) ?? null

  /* the euro line — recalculated from the window that is actually set, at the
     current selection's average gift cost. */
  const cadeauxParAn = Math.floor(365 / Math.max(1, jours))
  const coutAnnuel = cadeauxParAn * d.avgGiftCost

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
      <Retour />
      <h2 className="t-page" style={{ margin: 0 }}>Rythme &amp; éligibilité</h2>
      <p className="f-help" style={{ margin: 0 }}>
        Ce que votre fenêtre de participation coûte, et ce qu’elle fait vivre au client.
        Elle se règle dans votre établissement — ici on la lit.
      </p>

      <Card>
        <p className="t-card" style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {windowSentence(jours)}
          {forme && <Pill tone="ok">{forme.label}</Pill>}
          {jours === plancher && <Pill tone="ink">rythme du secteur</Pill>}
        </p>
        <p className="f-help" style={{ margin: '6px 0 0' }}>
          {/* the shape's typical sectors help you recognise it — until the merchant
              has named his own, at which point saying « le rythme de salon,
              institut » to a restaurant is noise in front of the line that matters */}
          {forme
            ? <><b>{forme.label}</b> = {forme.plain}{secteur ? '. ' : <> — le rythme de {forme.sectors}. </>}</>
            : <>Intervalle fixé à la main. </>}
          {jours === plancher
            ? <>C’est le rythme de votre secteur&nbsp;: {secteurLabel(secteur)}.</>
            : <>Le rythme de votre secteur ({secteurLabel(secteur)}) est de {plancher} jour{plancher > 1 ? 's' : ''}&nbsp;; vous l’avez allongé.</>}
        </p>
        <div className="actions">
          <Button size="sm" variant="quiet"
            onClick={() => nav(`/etablissement/${lieu?.id ?? 'l0'}?vue=reglages`)}>
            Modifier dans {lieu?.name ?? 'votre établissement'}
          </Button>
        </div>
      </Card>

      <Note tone="ok">
        À ce rythme, un client fidèle peut recevoir jusqu’à <b>{cadeauxParAn} cadeau
        {cadeauxParAn > 1 ? 'x' : ''} par an</b>, soit environ <b>{euros(coutAnnuel)} par
        client</b> au coût moyen de votre sélection actuelle.
      </Note>

      <KV rows={[
        ['Établissements concernés', d.scope],
        ['Fréquence des invitations publiques', <span key="f"><b>{d.publicFrequencyDays} jours</b> — réglage séparé, dans Exposition des liens d’avis</span>],
      ]} />

      <Note tone="ok">
        Pendant le délai, le client voit ses cadeaux actifs et sa date de déblocage —
        jamais un refus sec. Le lendemain, il peut rejouer&nbsp;:
        {' '}<a href="#" onClick={e => { e.preventDefault(); nav('/messages/revenez-jouer') }}>le message « Revenez jouer »</a> part ce jour-là.
      </Note>

      <Card>
        <p className="f-help" style={{ margin: 0 }}><b>Comment les deux fenêtres s’articulent&nbsp;:</b></p>
        <p className="t-code" style={{ marginTop: 6, overflowX: 'auto' }}>
          scan 14:00 ──[{d.sessionWindowHours} h : même expérience, même cadeau]── 18:00
          ──[{jours} j : cadeaux visibles, pas de nouveau tirage]── J+{jours} → étape suivante
        </p>
      </Card>

      <Note tone="candour">
        Raccourcir sous {plancher} jour{plancher > 1 ? 's' : ''} n’est pas proposé&nbsp;; allonger
        l’est toujours. Ce plancher est celui de votre secteur — le changer de secteur le
        déplace, sans toucher à vos cadeaux ni à votre roue.
        {' '}<a href="#" onClick={e => { e.preventDefault(); nav(`/etablissement/${lieu?.id ?? 'l0'}?vue=reglages`) }}>Secteur et fenêtre</a>.
      </Note>
    </div>
  )
}
